import { AdSurgeLogoText, ArrowDown, Check, Income, ReportUp, SearchBold } from '@tencent/odc-iconpack';
import { Button, DropMenu, GlobalSearchModal, message } from 'odc-shared/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ModuleSupportCenterCatalog } from '@src/api/generated/data-contracts';
import CopyRight from '@src/components/CopyRight';
import website from '@src/constants/website';
import useMobxSelector from '@src/hooks/useMobxSelector';
import { IArticle } from '@src/types/support.type';

import Content from './components/content';
import GettingStarted from './components/gettingStarted';
import Menu from './components/menu';
import useSearch from './hooks/useSearch';
import useSupportSearch from './hooks/useSupportSearch';

import './index.scss';

message.config({
  top: 80,
});

/**
 * 将 ModuleSupportCenterCatalog 转换为 IArticle 格式
 */
const transformCatalogToArticle = (catalog: ModuleSupportCenterCatalog): IArticle => {
  const hasChildren = catalog.children && catalog.children.length > 0;
  const isLeafWithPost = catalog.postId && !hasChildren;
  return {
    name: catalog.name || 'Untitled',
    aid: catalog.postId || catalog.id,
    // 只有叶子节点且有 postId 时才设置 article 字段
    article: isLeafWithPost ? catalog.postId : undefined,
    // 如果有子节点，递归转换
    subList: hasChildren ? catalog.children?.map(transformCatalogToArticle) : undefined,
  };
};

/**
 * 递归收集所有有子节点的目录 ID，用于默认展开
 */
const collectExpandableIds = (catalogsMap: Map<string, ModuleSupportCenterCatalog>, pid?: string): string[] => {
  const ids: string[] = [];
  if (!pid) {
    return ids;
  }
  const traverse = (id: string) => {
    const parentId = catalogsMap.get(id)?.parentId;
    if (parentId) {
      ids.push(parentId);
      traverse(parentId);
    }
  };
  traverse(pid);
  return ids;
};

const SupportCenter = () => {
  const navigate = useNavigate();

  const [defaultExpandedIds, setDefaultExpandedIds] = useState<string[]>([]);

  // 文档类型相关
  const { params, updateParams } = useSearch();
  const [openCategory, setOpenCategory] = useState<boolean>(false);

  // 使用搜索 hook
  const {
    searchState,
    searchSuggestions,
    searchResults,
    showGlobalSearch,
    setShowGlobalSearch,
    handleSearchChange,
    handleSearchClose,
    handleViewMore,
  } = useSupportSearch();

  // 从 store 获取目录数据
  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);
  const catalogsMap = useMobxSelector((stores) => stores.helpCenterStore.catalogsMap);

  // 根据当前平台转换目录数据为 Menu 组件需要的格式
  const navData = useMemo(() => {
    const currentCatalogs = catalogsByPlatform[params.platform || 'publisher'] || [];
    return currentCatalogs.map(transformCatalogToArticle);
  }, [catalogsByPlatform, params.platform]);

  useEffect(() => {
    const expandableIds = collectExpandableIds(catalogsMap, params.pid);
    setDefaultExpandedIds(expandableIds);
  }, [catalogsMap, params.pid]);

  return (
    <div className="odc-support-center">
      <div className="support-center-header">
        <div className="official-logo">
          <AdSurgeLogoText className="logo-brand" onClick={() => navigate('/')} />
          <div className="!ml-3r">
            <DropMenu
              data={[
                {
                  value: 'advertiser',
                  label: (<div className="odc-category-dropmenu-item">
                    User growth
                    {params.platform === 'advertiser' && <Check className="!text-md" />}
                  </div>
                  ),
                },
                {
                  value: 'publisher',
                  label: (<div className="odc-category-dropmenu-item">
                    Monetization
                    {params.platform === 'publisher' && <Check className="!text-md" />}
                  </div>),
                },
              ]}
              offsetData={{
                top: -8,
              }}
              dropdownPortalClassName="odc-docs-category-dropmenu"
              onClick={(value: number | string) => {
                updateParams({ platform: value as 'publisher' | 'advertiser', pid: '' });
                setOpenCategory(false);
              }}
              open={openCategory}
              trigger="click"
              matchTriggerWidth
            >
              <div
                className="odc-docs-category-trigger !px-3r w-148"
                onClick={() => setOpenCategory(true)}
              >
                {params.platform === 'publisher' ? 'Monetization' : 'User Growth'}
                <ArrowDown className="!ml-3r text-base" />
              </div>
            </DropMenu>
          </div>
        </div>
        <div className="options">
          <div className="help-center-search" onClick={() => setShowGlobalSearch(true)}>
            <div className="help-center-search-icon">
              <SearchBold />
            </div>
            <div className="help-center-search-placeholder">Search by topic or keyword</div>
          </div>
          <DropMenu
            data={[
              { value: 'userGrowth', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><ReportUp /></div>User Growth</div>) },
              { value: 'monetization', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><Income /></div>Monetization</div>) },
            ]}
            offsetData={{
              top: 0,
            }}
            dropdownPortalClassName="odc-login-dropmenu"
            onClick={(value: number | string) => {
              if (value === 'userGrowth') {
                window.location.href = website.userGrowth;
              } else if (value === 'monetization') {
                window.location.href = website.monetization;
              }
            }}
            trigger="hover"
          >
            <Button type="primary">
              Log in
            </Button>
          </DropMenu>
        </div>
      </div>
      <div className="support-center-main">
        <div className="support-center-menu">
          <Menu
            navData={navData}
            defaultExpandedIds={defaultExpandedIds}
            activeId={params.pid}
            setPid={(pid: string) => updateParams({ pid })}
            platform={params.platform}
          />
        </div>
        <div className="support-center-content">
          {
            params.pid && catalogsMap.has(params.pid) ? (
              <Content
                key={params.pid}
                pid={params.pid}
                title={catalogsMap.get(params.pid)?.name || ''}
              />
            ) : (
              <GettingStarted
                platform={params.platform}
                setPid={(pid: string | undefined) => updateParams({ pid })}
              />
            )
          }
        </div>
      </div>
      <CopyRight />
      <GlobalSearchModal
        type="panel"
        escTag={true}
        placeholder="Search by topic or keyword"
        show={showGlobalSearch}
        value={searchState.keyword}
        suggestions={searchSuggestions}
        results={searchResults}
        resultsCount={searchState.total}
        onChange={handleSearchChange}
        onClose={handleSearchClose}
        onViewMore={handleViewMore}
      />
    </div>
  );
};
export default SupportCenter;